/**
 * Tenants API Client
 */

import { getHttpClient } from '@spcc/core'
import { TENANT_ENDPOINTS, TenantDto } from '../../api/api-types'
import {
  transformFilterToQuery,
  transformToCreateRequest,
  transformToUpdateRequest,
  transformDtoToTenant,
  transformDtosToTenants,
} from '../../api/transformers'
import type {
  CustomerGroupOption,
  Tenant,
  TenantsFilter,
  TenantsListResponse,
  PaginationMeta,
  TenantStats,
} from '../../domain/types/tenant.types'

export const tenantsApi = {
  async getList(filter?: TenantsFilter): Promise<TenantsListResponse> {
    const http = getHttpClient()
    const defaultFilter: TenantsFilter = { page: 1, pageSize: 10, ...filter }
    const params = transformFilterToQuery(defaultFilter)

    const response = await http.get<unknown>(TENANT_ENDPOINTS.LIST, { params })
    const responseData = response.data as Record<string, unknown>

    // Strapi format: { data: [...], meta: { pagination: {...} } }
    const raw = Array.isArray(responseData?.data)
      ? (responseData.data as TenantDto[])
      : []

    const tenants = transformDtosToTenants(raw)

    const meta = responseData?.meta as Record<string, unknown> | undefined
    const paginationData = meta?.pagination as Record<string, unknown> | undefined

    const pagination: PaginationMeta = {
      page: (paginationData?.page as number) ?? defaultFilter.page ?? 1,
      pageSize: (paginationData?.pageSize as number) ?? defaultFilter.pageSize ?? 10,
      pageCount: (paginationData?.pageCount as number) ?? 1,
      total: (paginationData?.total as number) ?? tenants.length,
    }

    const statsData = meta?.stats as Record<string, unknown> | undefined
    const globalStats: TenantStats | undefined = statsData
      ? {
          total: (statsData.total as number) ?? 0,
          active: (statsData.active as number) ?? 0,
          pending: (statsData.pending as number) ?? 0,
          suspended: (statsData.suspended as number) ?? 0,
        }
      : undefined

    return {
      data: tenants,
      meta: pagination,
      globalStats,
    }
  },

  async getById(id: string): Promise<Tenant> {
    const http = getHttpClient()
    const response = await http.get<unknown>(
      TENANT_ENDPOINTS.DETAIL(id),
      { params: { 'populate[customerGroup]': true, 'populate[attachments]': true } }
    )
    const responseData = response.data as Record<string, unknown>
    // Strapi wraps single items: { data: {...} }
    const dto = (responseData.data ?? responseData) as TenantDto
    return transformDtoToTenant(dto)
  },

  async create(data: Omit<Tenant, 'id' | 'documentId' | 'createdAt' | 'updatedAt'>): Promise<Tenant> {
    const http = getHttpClient()
    const request = transformToCreateRequest({
      ...data,
      customerGroupId: data.customerGroup ? Number(data.customerGroup.id) : undefined,
    })
    const response = await http.post<unknown>(
      TENANT_ENDPOINTS.CREATE,
      { data: request }
    )
    const responseData = response.data as Record<string, unknown>
    const dto = (responseData.data ?? responseData) as TenantDto
    return transformDtoToTenant(dto)
  },

  async update(
    id: string,
    data: Partial<Tenant>,
    customerGroupId?: string
  ): Promise<Tenant> {
    const http = getHttpClient()
    const request = transformToUpdateRequest({
      ...data,
      customerGroupId: customerGroupId ? Number(customerGroupId) : undefined,
    })
    const response = await http.put<unknown>(
      TENANT_ENDPOINTS.UPDATE(id),
      { data: request }
    )
    const responseData = response.data as Record<string, unknown>
    const dto = (responseData.data ?? responseData) as TenantDto
    return transformDtoToTenant(dto)
  },

  async delete(id: string): Promise<void> {
    const http = getHttpClient()
    await http.delete(TENANT_ENDPOINTS.DELETE(id))
  },

  async approve(
    id: string,
    unblockUsers?: boolean,
    customerGroupDocumentId?: string,
    sapCustomerGroup?: string
  ): Promise<Tenant> {
    const http = getHttpClient()
    const response = await http.post<unknown>(
      TENANT_ENDPOINTS.APPROVE(id),
      { data: { unblockUsers, customerGroupDocumentId, sapCustomerGroup } }
    )
    const responseData = response.data as Record<string, unknown>
    const dto = (responseData.data ?? responseData) as TenantDto
    return transformDtoToTenant(dto)
  },

  async suspend(id: string): Promise<Tenant> {
    const http = getHttpClient()
    const response = await http.post<unknown>(TENANT_ENDPOINTS.SUSPEND(id))
    const responseData = response.data as Record<string, unknown>
    const dto = (responseData.data ?? responseData) as TenantDto
    return transformDtoToTenant(dto)
  },

  async fetchCustomerGroups(customerType?: 'local' | 'foreign' | 'one_time'): Promise<CustomerGroupOption[]> {
    const http = getHttpClient()
    const params: Record<string, unknown> = {
      'filters[isActive][$eq]': true,
      'sort': 'sortOrder:asc',
      'pagination[pageSize]': 100,
    }
    if (customerType) {
      params['filters[customerType][$eq]'] = customerType
    }
    const response = await http.get<unknown>('/api/customer-groups', { params })
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data) ? responseData.data : []
    return raw.map((item: any) => ({
      id: String(item.id),
      documentId: item.documentId || String(item.id),
      name: item.name || '',
      code: item.code || '',
      customerType: item.customerType || 'local',
      sapCustomerAccountGroup: item.sapCustomerAccountGroup || '',
      sapBpGrouping: item.sapBpGrouping || '',
      sapReconciliationAccount: item.sapReconciliationAccount || '',
    }))
  },

  async updateStatus(id: string, status: Tenant['status']): Promise<Tenant> {
    const http = getHttpClient()
    const response = await http.put<unknown>(
      TENANT_ENDPOINTS.UPDATE_STATUS(id),
      { data: { tenantStatus: status } }
    )
    const responseData = response.data as Record<string, unknown>
    const dto = (responseData.data ?? responseData) as TenantDto
    return transformDtoToTenant(dto)
  },
}
